<template>
  <div class="summary-detail-page">
    <div class="header">
      <van-nav-bar
        :title="$t('task_execution_detail')"
        :left-text="$t('btn_prev')"
        left-arrow
        @click-left="onClickLeft"
      />
    </div>

    <!-- 顶栏统计区域 -->
    <div class="stats-container">
      <div class="stats-card">
        <div class="quarter-switcher">
          <div
            class="quarter-switch-btn"
            :class="{ disabled: loading }"
            @click="changeQuarter(-1)"
          >
            <van-icon name="arrow-left" />
          </div>
          <div class="quarter-label">{{ selectedQuarter }}</div>
          <div
            class="quarter-switch-btn"
            :class="{ disabled: !canGoNextQuarter || loading }"
            @click="changeQuarter(1)"
          >
            <van-icon name="arrow" />
          </div>
        </div>
        <div v-if="statsData" class="stats-header">
          <span class="stats-title">{{ $t('task_data_overview') }}{{ statsData.quarter ? ` (${statsData.quarter})` : '' }}</span>
          <span class="stats-note">{{ $t('task_kpi_updated_yesterday') }}</span>
        </div>
        <div v-if="statsData" class="stats-grid">
          <div class="stats-row">
            <div class="stats-item">
              <div class="label">{{ $t('task_quarter_send') }}</div>
              <div class="value">{{ statsData.send_count || 0 }}<span class="unit">{{ $t('task_times') }}</span></div>
            </div>
            <div class="stats-item">
              <div class="label">{{ $t('task_quarter_open') }}</div>
              <div class="value highlight">{{ statsData.open_count || 0 }}<span class="unit">{{ $t('task_times') }}</span></div>
            </div>
          </div>

          <div class="stats-divider"></div>

          <div class="stats-month-row">
            <div v-if="statsData.m1" class="month-col">
              <div class="month-name">{{ formatMonth(statsData.m1.month) }}</div>
              <div class="month-data">
                <div>{{ $t('task_send') }} <span class="count">{{ statsData.m1.send_count || 0 }}</span></div>
                <div>{{ $t('task_open') }} <span class="count highlight">{{ statsData.m1.open_count || 0 }}</span></div>
              </div>
            </div>
            <div v-if="statsData.m2" class="month-col">
              <div class="month-name">{{ formatMonth(statsData.m2.month) }}</div>
              <div class="month-data">
                <div>{{ $t('task_send') }} <span class="count">{{ statsData.m2.send_count || 0 }}</span></div>
                <div>{{ $t('task_open') }} <span class="count highlight">{{ statsData.m2.open_count || 0 }}</span></div>
              </div>
            </div>
            <div v-if="statsData.m3" class="month-col">
              <div class="month-name">{{ formatMonth(statsData.m3.month) }}</div>
              <div class="month-data">
                <div>{{ $t('task_send') }} <span class="count">{{ statsData.m3.send_count || 0 }}</span></div>
                <div>{{ $t('task_open') }} <span class="count highlight">{{ statsData.m3.open_count || 0 }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <van-list
      v-model="loading"
      :finished="finished"
      :finished-text="$t('no_more_data')"
      @load="onLoad"
    >
      <div v-for="(physician, pIndex) in list" :key="pIndex" class="physician-card">
        <!-- 医生信息头部 - 参考 task.vue 样式 -->
        <div class="physician-header" @click="toggleExpand(pIndex)">
          <div class="header-left">
            <div class="avatar-column">
              <img :src="getAvatar(physician)" class="avatar" />
            </div>
            <div class="physician-info-wrapper">
              <div class="user-row">
                <span class="user-name">{{ physician.physician_name | displayPhysicianName }}</span>
                <!-- <span class="title-tag">{{ physician.title || '主治医师' }}</span> -->
              </div>
              <div class="hospital-row">
                <span class="hospital-name">{{ physician.institution_name }}</span>
                <span class="department-name">{{ physician.department_name }}</span>
              </div>
            </div>
          </div>
          <div class="header-right">
            <span class="stats-text">
              {{ $t('task_send') }} <span class="highlight-black">{{ physician.send_count || 0 }}</span> / {{ $t('task_open') }} <span class="highlight-red">{{ physician.open_count || 0 }}</span>
            </span>
            <van-icon name="arrow-down" :class="{'rotate-up': physician.isExpanded}" />
          </div>
        </div>

        <!-- 互动历史列表 -->
        <div v-show="physician.isExpanded" class="interaction-list">
          <div v-for="(item, index) in physician.actions" :key="index" class="interaction-item">
            <div class="time-line">
              <div class="dot" :class="item.type"></div>
              <div v-if="index !== physician.actions.length - 1" class="line"></div>
            </div>
            <div class="content-wrapper">
              <div class="time-row">
                <span class="time">{{ formatTime(item.action_time) }}</span>
                <span class="action-tag" :class="item.type">
                  {{ item.type === 'send' ? $t('task_send') : $t('task_open') }}
                </span>
                <span v-if="item.type === 'open'" class="read-duration-inline">
                  <van-icon name="clock-o" /> {{ formatDuration(item.duration) }}
                </span>
              </div>
              <div class="content-box">
                <div class="content-main">
                  <div class="content-detail">
                    <div class="content-name">{{ item.content_name }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-list>
  </div>
</template>

<script>
import { NavBar, List, Tag, Icon } from 'vant';
import { getContentSendOpenStats, getPhysicianContentSendOpenStats } from '@/api/content';

export default {
  name: 'TaskSummaryDetail',
  components: {
    [NavBar.name]: NavBar,
    [List.name]: List,
    [Tag.name]: Tag,
    [Icon.name]: Icon
  },
  data() {
    return {
      list: [],
      loading: false,
      finished: false,
      statsData: null,
      userId: '',
      selectedQuarter: '',
      currentQuarter: '',
      page_index: 1,
      page_size: 10
    };
  },
  computed: {
    canGoNextQuarter() {
      return this.selectedQuarter && this.selectedQuarter !== this.currentQuarter;
    }
  },
  created() {
    this.userId = localStorage.getItem('user_id') || '';
    this.currentQuarter = this.getQuarterString(new Date());
    this.selectedQuarter = this.currentQuarter;
    this.fetchStats();
  },
  methods: {
    getQuarterString(date) {
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${year}Q${quarter}`;
    },
    getQuarterDate(quarter) {
      const match = /^(\d{4})Q([1-4])$/.exec(quarter);
      if (!match) return new Date();
      const year = Number(match[1]);
      const quarterIndex = Number(match[2]) - 1;
      return new Date(year, quarterIndex * 3, 1);
    },
    shiftQuarter(quarter, offset) {
      const date = this.getQuarterDate(quarter);
      date.setMonth(date.getMonth() + offset * 3);
      return this.getQuarterString(date);
    },
    async changeQuarter(offset) {
      if (offset > 0 && !this.canGoNextQuarter) return;
      if (this.loading) return;

      this.selectedQuarter = this.shiftQuarter(this.selectedQuarter, offset);
      await this.reloadQuarterData();
    },
    async reloadQuarterData() {
      this.statsData = null;
      this.list = [];
      this.page_index = 1;
      this.finished = false;
      this.loading = true;

      await this.fetchStats();
      await this.onLoad();
    },
    async fetchStats() {
      try {
        const res = await getContentSendOpenStats({ quarter: this.selectedQuarter, user_id: this.userId });
        if (res && res.isSuccess && res.data) {
          this.statsData = res.data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    formatMonth(month) {
      if (!month) return '';
      const parts = month.split('-');
      if (parts.length === 2) {
        return `${parseInt(parts[1], 10)}${this.$t('task_month')}`;
      }
      return month;
    },
    onClickLeft() {
      this.$router.back();
    },
    toggleExpand(index) {
      if (this.list[index]) {
        this.$set(this.list[index], 'isExpanded', !this.list[index].isExpanded);
      }
    },
    getAvatar(physician) {
      // 简单的头像逻辑，实际项目中可能需要引入 assert 图片
      return physician.sex === '女'
        ? require('@/assets/lundbeck/avatar-female.png')
        : require('@/assets/lundbeck/avatar-male.png');
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      // Parse ISO string and format to YYYY-MM-DD HH:mm
      const date = new Date(timeStr);
      if (isNaN(date.getTime())) return timeStr;

      const padObj = (num) => (num < 10 ? '0' + num : num);
      const y = date.getFullYear();
      const m = padObj(date.getMonth() + 1);
      const d = padObj(date.getDate());
      const h = padObj(date.getHours());
      const min = padObj(date.getMinutes());
      return `${y}-${m}-${d} ${h}:${min}`;
    },
    formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0:00';
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      return `${min}:${String(sec).padStart(2, '0')}`;
    },
    async onLoad() {
      try {
        const res = await getPhysicianContentSendOpenStats({
          quarter: this.selectedQuarter,
          user_id: this.userId,
          page_index: this.page_index,
          page_size: this.page_size
        });

        if (res && res.isSuccess && res.data) {
          const newData = res.data.map(item => ({
            ...item,
            isExpanded: false, // Default to collapsed
            actions: (item.interaction_detail || []).map(detail => ({
              ...detail,
              type: detail.interaction_type,
              action_time: detail.interaction_time,
              duration: detail.duration
            }))
          }));

          this.list = this.list.concat(newData);
          this.page_index += 1;

          if (this.list.length >= res.data_count || res.data.length < this.page_size) {
            this.finished = true;
          }
        } else {
          this.finished = true;
        }
      } catch (err) {
        console.error(err);
        this.finished = true;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.summary-detail-page {
  min-height: 100vh;
  background-color: #f7f8fa;

  ::v-deep .van-nav-bar__text,
  ::v-deep .van-nav-bar__icon,
  ::v-deep .van-nav-bar__arrow {
    color: #00b852;
  }

  .stats-container {
    padding: 12px 12px 0;
    margin-bottom: 15px;

    .stats-card {
      background: #fff;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

      .quarter-switcher {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        gap: 12px;

        .quarter-label {
          min-width: 90px;
          text-align: center;
          font-size: 16px;
          font-weight: 600;
          color: #323233;
        }

        .quarter-switch-btn {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #00b852;
          background: rgba(0, 184, 82, 0.08);

          .van-icon {
            font-size: 12px;
          }

          &.disabled {
            color: #c8c9cc;
            background: #f7f8fa;
          }
        }
      }

      .stats-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 1px solid #f5f5f5;

        .stats-title {
          font-size: 15px;
          font-weight: 600;
          color: #323233;
          border-left: 3px solid #00b852;
          padding-left: 8px;
        }
      }

      .stats-note {
        font-size: 11px;
        color: #969799;
        margin-left: 8px;
        margin-bottom: 0;
        white-space: nowrap;
        text-align: right;
      }

      .stats-grid {
        .stats-row {
          display: flex;
          justify-content: space-between;

          .stats-item {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center; /* 左右居中 */

            &:first-child {
              // padding-right: 12px;
              border-right: 1px solid #f9f9f9;
            }
            // &:last-child {
            //   padding-left: 12px;
            // }

            .label {
              font-size: 12px;
              color: #969799;
              margin-bottom: 4px;
            }

            .value {
              font-size: 18px;
              font-weight: 600;
              color: #323233;
              font-family: DIN, -apple-system, BlinkMacSystemFont, sans-serif;

              &.highlight {
                color: #00b852;
              }

              .unit {
                font-size: 11px;
                font-weight: normal;
                margin-left: 2px;
                color: #969799;
              }
            }
          }
        }

        .stats-divider {
          height: 1px;
          background: #f5f5f5;
          margin: 16px 0 12px;
        }

        .stats-month-row {
          display: flex;
          justify-content: space-between;

          .month-col {
            flex: 1;
            text-align: center;

            &:not(:last-child) {
              border-right: 1px solid #f9f9f9;
            }

            .month-name {
              font-size: 13px;
              color: #323233;
              font-weight: 600;
              margin-bottom: 6px;
            }

            .month-data {
              font-size: 11px;
              color: #969799;
              line-height: 1.6;

              .count {
                color: #323233;
                font-family: DIN, -apple-system, BlinkMacSystemFont, sans-serif;
                font-weight: 600;
                margin-left: 2px;

                &.highlight {
                  color: #00b852;
                }
              }
            }
          }
        }
      }
    }
  }

  .physician-card {
    background: #fff;
    margin-bottom: 12px;
    padding: 0; // Reset padding to handle header/content separately
    border-radius: 8px;
    overflow: hidden; // Ensure children respect border radius
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02); // Add slight shadow for depth

    /* Physician Header Style */
    .physician-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      // padding-bottom: 12px;
      // border-bottom: 1px solid #f5f5f5;
      // margin-bottom: 12px;
      padding-bottom: 0; // Collapsed header shouldn't enforce bottom padding if not expanded, handle via interaction-list margin
      padding: 12px 16px; // Add padding back to header

      .header-left {
        display: flex;
        align-items: center;
        flex: 1;
      }

      .header-right {
        margin-left: 10px;
        color: #969799;
        font-size: 12px;
        display: flex;
        align-items: center;

        .stats-text {
          margin-right: 8px;
          font-size: 11px;
          color: #969799;

          .highlight-black {
            color: #323233;
            font-weight: 600;
            margin: 0 2px;
          }

          .highlight-red {
            color: #00b852;
            font-weight: 600;
            margin: 0 2px;
          }
        }

        .van-icon {
          transition: transform 0.3s;
          &.rotate-up {
            transform: rotate(180deg);
          }
        }
      }

      .avatar-column {
        margin-right: 10px; // Smaller margin
        .avatar {
          width: 36px; // Smaller avatar
          height: 36px;
          border-radius: 50%;
          border: 1px solid #eee;
        }
      }

      .physician-info-wrapper {
        flex: 1;

        .user-row {
          display: flex;
          align-items: center;
          margin-bottom: 2px; // Eliminate bottom margin

          .user-name {
            font-size: 14px; // Smaller font
            font-weight: 600;
            color: #323233;
            margin-right: 8px;
          }

          .title-tag {
            font-size: 10px;
            color: #fff;
            background-color: #00b852;
            padding: 1px 4px;
            border-radius: 4px;
            transform: scale(0.9);
            transform-origin: left center;
          }
        }

        .hospital-row {
          font-size: 12px; // Smaller font
          color: #969799;

          .hospital-name {
            margin-right: 8px;
          }
        }
      }
    }

    /* Interaction Timeline Style */
    .interaction-list {
      // padding-left: 8px;
      // margin-top: 12px; // Add top margin only when visible
      // border-top: 1px solid #f5f5f5; // Move border here
      // padding-top: 12px;

      background-color: #fcfcfc; // Slightly off-white background for content area
      border-top: 1px solid #f0f0f0;
      padding: 16px; // Add padding to content area

      .interaction-item {
        display: flex;
        position: relative;
        padding-bottom: 20px;

        &:last-child {
          padding-bottom: 0;
        }

        .time-line {
          width: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-right: 12px;

          .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            margin-top: 6px;
            z-index: 1;

            &.send { background-color: #c8c9cc; } // Lighter grey for inactive
            &.open { background-color: #00b852; }
          }

          .line {
            flex: 1;
            width: 1px;
            background-color: #ebedf0;
            margin-top: 4px;
          }
        }

        .content-wrapper {
          flex: 1;

          .time-row {
            display: flex;
            align-items: center;
            margin-bottom: 6px;

            .time {
              font-size: 12px;
              color: #969799;
              margin-right: 8px;
              width: 110px; /* 固定宽度对齐 */
            }

            .action-tag {
              font-size: 10px;
              padding: 3px 8px;
              border-radius: 4px;
              font-weight: 600;

              &.send {
                background: #f5f5f5;
                color: #969799;
              }

              &.open {
                background: rgba(0, 184, 82, 0.1);
                color: #00b852;
              }
            }

            .read-duration-inline {
              font-size: 11px;
              color: #969799;
              display: inline-flex;
              align-items: center;
              margin-left: auto; /* 设置为 auto 靠右对齐 */

              .van-icon {
                margin-right: 2px;
                font-size: 12px;
              }
            }
          }

          .content-box {
            .content-main {
              display: flex;
              // align-items: flex-start;

              .content-detail {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: center; // Center vertically if separate from thumb

                .content-name {
                  font-size: 13px;
                  color: #333;
                  line-height: 1.4;
                  margin-bottom: 4px;
                  display: -webkit-box;
                  line-clamp: 2;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
